/**
 * Single source of truth for the HPO clinical-modifier qualifiers added by
 * PTV2-1169. Each qualifier is a `VocabularyTermPicker` scoped to a specific
 * HPO subtree (the term ids come straight from the ticket) and serialises to
 * a `featureQualifierGroups[*]` entry keyed by its wire `type`.
 *
 * The editor (picker rendering), the read mapper (wire → view model), and the
 * form transforms (view model ↔ wire) all iterate this table, so a fifth
 * modifier is a one-line addition here plus its i18n keys under
 * `patient.phenotypes.fields.<key>` / `<key>Label` / `<key>Placeholder`.
 *
 * Onset and severity are *also* clinical modifiers but keep dedicated flat
 * fields and wire types (`AGE_OF_ONSET` / `SEVERITY`) for legacy reasons; they
 * are intentionally not in this list.
 */

import type { LiveFeatureQualifierType } from '@/api/client';
import type { PhenotypeModifierKey } from '@/features/patient-record/types';

export interface ModifierSpec {
  readonly key: PhenotypeModifierKey;
  /** Wire qualifier `type` this modifier serialises to. */
  readonly wireType: LiveFeatureQualifierType;
  /** HPO subtree root; the picker scopes its results to this term's descendants. */
  readonly subtreeRootId: string;
}

/** The four modifier qualifiers extended in PTV2-1169, in editor display order. */
export const MODIFIER_SPECS: readonly ModifierSpec[] = [
  {
    key: 'paceOfProgression',
    wireType: 'PACE_OF_PROGRESSION',
    subtreeRootId: 'HP:0003679',
  },
  {
    key: 'temporalPattern',
    wireType: 'TEMPORAL_PATTERN',
    subtreeRootId: 'HP:0011008',
  },
  {
    key: 'spatialPattern',
    wireType: 'SPATIAL_PATTERN',
    subtreeRootId: 'HP:0012836',
  },
  {
    key: 'laterality',
    wireType: 'LATERALITY',
    subtreeRootId: 'HP:0012831',
  },
];
